import React from 'react';
import type { TimeJob, TimePerson } from '@/data/scheduleTimeTracking';
import SearchSelect from '../components/form/SearchSelect';
import TimeEmployeePicker from '../components/time/TimeEmployeePicker';
import { useDebouncedSearch } from '../components/time/useDebouncedSearch';
import { useTimeJobLists } from '../components/time/useTimeJobLists';

const jobKey = (job: TimeJob) => `${job.kind}:${job.id}`;

/** Keep selections independent of search pages and background refreshes. */
export default function ManualTimeEntryFields({ locationId, authorizationKey, busy, person, job, onPerson, onJob, validation }: {
  locationId: string; authorizationKey: string; busy: boolean; person: TimePerson | null; job: TimeJob | null;
  onPerson: (value: TimePerson | null) => void; onJob: (value: TimeJob | null) => void; validation: Record<string, string>;
}) {
  const [jobSearch, setJobSearch] = React.useState('');
  // The job searches start when the Job field first opens, not when the form does.
  const [jobsOpened, setJobsOpened] = React.useState(false);
  const jobTerm = useDebouncedSearch(jobSearch);
  // One Job field searches every kind of job; each option names its kind.
  const lists = useTimeJobLists({ source: 'manual', locationId, authorizationKey, search: jobTerm, enabled: jobsOpened });
  const jobChoices = lists.choices.map(({ job: row, kindLabel }) => ({
    job: row, option: { id: jobKey(row), label: `${row.code} · ${row.name}`, detail: `${kindLabel} · ${row.status.replace(/_/g, ' ')}` },
  }));
  const id = React.useId();
  return <div className="schedule-time__manual-fields">
    <div className="schedule-time__fields">
      <TimeEmployeePicker id={`${id}-person`} locationId={locationId} authorizationKey={authorizationKey} value={person} onChange={onPerson} disabled={busy}
        invalid={Boolean(validation.person)} describedBy={validation.person ? `${id}-person-error` : undefined}>
        {validation.person ? <span className="schedule-time__error" id={`${id}-person-error`}>{validation.person}</span> : null}
      </TimeEmployeePicker>
      <SearchSelect id={`${id}-job`} label="Job" value={job ? { id: jobKey(job), label: `${job.code} · ${job.name}` } : null}
        options={jobChoices.map(choice => choice.option)} search={jobSearch} onSearchChange={text => { setJobsOpened(true); setJobSearch(text); }}
        onChange={option => onJob(jobChoices.find(choice => choice.option.id === option.id)?.job ?? null)}
        placeholder="Type a job code or name" loading={lists.loading || jobTerm !== jobSearch.trim()}
        error={lists.error} onRetry={lists.retry}
        emptyText="No matching jobs. Try another name or code." hasMore={lists.hasMore}
        loadingMore={lists.loadingMore} loadMoreLabel="Load more jobs" onLoadMore={lists.loadMore}
        required disabled={busy} invalid={Boolean(validation.job)} describedBy={validation.job ? `${id}-job-error` : undefined}>
        {validation.job ? <span className="schedule-time__error" id={`${id}-job-error`}>{validation.job}</span> : null}
      </SearchSelect>
    </div>
    <p className="fg-muted text-sm">Completed jobs are included. You can add time only for employees you are authorized to manage.</p>
  </div>;
}
